import { ReactNode, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BellRing,
  LayoutDashboard,
  Users,
  Building,
  CalendarDays,
  FileText,
  ScrollText,
  DollarSign,
  Wallet,
  Megaphone,
  Wrench,
  Shield,
  ClipboardList,
  ListChecks,
  HelpCircle,
  LogOut,
  Menu,
  X,
  Globe,
  GitBranch,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/cn";
import { Logo } from "@/components/Logo";
import { AssistantWidget } from "@/components/assistant/AssistantWidget";
import { InfoPopover } from "@/components/InfoPopover";
import { useFirstLoginTour } from "@/hooks/useFirstLoginTour";
import { SCREENS } from "@/data/screens";
import { userHasCapability } from "@costadorada/shared";
import type { Capability } from "@costadorada/shared";

type Role = "ADMIN" | "BOARD_MEMBER" | "RESIDENT" | "PUBLIC";

export interface NavItem {
  to: string;
  labelKey: string;
  icon: ReactNode;
  roles: Role[];
  // Shown also to users holding this capability, regardless of role
  capability?: Capability;
  tour?: string;
}

export interface NavSection {
  // Sections without a labelKey render no header (top: Dashboard; bottom: Help).
  labelKey?: string;
  items: NavItem[];
}

// Financial items are grouped last: every role that can see money in this
// sidebar (a resident's own fees, or the board's finance:manage module) sees
// that group at the bottom, after governance, resident services, and admin.
// Also read by Administración → Roles y permisos, so that page shows exactly this.
export const navSections: NavSection[] = [
  {
    items: [
      { to: "/dashboard", labelKey: "nav.dashboard", icon: <LayoutDashboard size={18} />, roles: ["ADMIN", "BOARD_MEMBER", "RESIDENT"] },
    ],
  },
  {
    labelKey: "nav.sectionGovernance",
    items: [
      { to: "/meetings", labelKey: "nav.meetings", icon: <CalendarDays size={18} />, roles: ["ADMIN", "BOARD_MEMBER"], tour: "nav-meetings" },
      { to: "/resolutions", labelKey: "nav.resolutions", icon: <ScrollText size={18} />, roles: ["ADMIN", "BOARD_MEMBER"], tour: "nav-resolutions" },
      { to: "/documents", labelKey: "nav.documents", icon: <FileText size={18} />, roles: ["ADMIN", "BOARD_MEMBER", "RESIDENT"], tour: "nav-documents" },
      { to: "/contracts", labelKey: "nav.contracts", icon: <FileText size={18} />, roles: ["ADMIN", "BOARD_MEMBER"], tour: "nav-contracts" },
      { to: "/legal-actions", labelKey: "nav.legal", icon: <FileText size={18} />, roles: ["ADMIN", "BOARD_MEMBER"], tour: "nav-legal" },
      { to: "/operations-queue", labelKey: "nav.operationsQueue", icon: <ListChecks size={18} />, roles: ["ADMIN", "BOARD_MEMBER"], tour: "nav-operations-queue" },
    ],
  },
  {
    labelKey: "nav.resident",
    items: [
      { to: "/resident/works-permit", labelKey: "nav.worksPermits", icon: <Wrench size={18} />, roles: ["RESIDENT", "ADMIN", "BOARD_MEMBER"], tour: "nav-works-permit" },
      { to: "/resident/maintenance", labelKey: "nav.maintenance", icon: <Wrench size={18} />, roles: ["RESIDENT", "ADMIN", "BOARD_MEMBER"], tour: "nav-maintenance" },
      { to: "/resident/announcements", labelKey: "nav.announcements", icon: <Megaphone size={18} />, roles: ["RESIDENT", "ADMIN", "BOARD_MEMBER"], tour: "nav-announcements" },
    ],
  },
  {
    labelKey: "nav.sectionSecurity",
    items: [
      { to: "/admin/incidents", labelKey: "nav.incidents", icon: <Shield size={18} />, roles: ["ADMIN", "BOARD_MEMBER"], tour: "nav-incidents" },
      { to: "/admin/keys", labelKey: "nav.keys", icon: <Shield size={18} />, roles: ["ADMIN"], tour: "nav-keys" },
      { to: "/admin/cameras", labelKey: "security.cameras", icon: <Shield size={18} />, roles: ["ADMIN"], tour: "nav-cameras" },
    ],
  },
  {
    labelKey: "nav.admin",
    items: [
      { to: "/admin/users", labelKey: "nav.users", icon: <Users size={18} />, roles: ["ADMIN"], tour: "nav-users" },
      { to: "/admin/roles", labelKey: "nav.roles", icon: <Shield size={18} />, roles: ["ADMIN"] },
      { to: "/admin/staff", labelKey: "nav.staff", icon: <Users size={18} />, roles: ["ADMIN"], tour: "nav-staff" },
      { to: "/admin/units", labelKey: "nav.units", icon: <Building size={18} />, roles: ["ADMIN"], tour: "nav-units" },
      { to: "/admin/audit-log", labelKey: "nav.auditLog", icon: <ClipboardList size={18} />, roles: ["ADMIN"], tour: "nav-audit" },
    ],
  },
  {
    labelKey: "nav.sectionFinance",
    items: [
      { to: "/finance/dashboard", labelKey: "nav.financeDashboard", icon: <DollarSign size={18} />, roles: ["ADMIN", "BOARD_MEMBER"], capability: "finance:manage", tour: "nav-finance" },
      { to: "/finance/fees", labelKey: "nav.fees", icon: <DollarSign size={18} />, roles: ["ADMIN", "BOARD_MEMBER"], capability: "finance:manage", tour: "nav-fees" },
      { to: "/finance/reminders", labelKey: "nav.feeReminders", icon: <BellRing size={18} />, roles: ["ADMIN", "BOARD_MEMBER"], capability: "finance:manage" },
      { to: "/finance/expenses", labelKey: "nav.expenses", icon: <DollarSign size={18} />, roles: ["ADMIN", "BOARD_MEMBER"], capability: "finance:manage", tour: "nav-expenses" },
      { to: "/finance/reports", labelKey: "nav.reports", icon: <FileText size={18} />, roles: ["ADMIN", "BOARD_MEMBER"], capability: "finance:manage", tour: "nav-reports" },
      { to: "/finance/budgets", labelKey: "nav.budgets", icon: <Wallet size={18} />, roles: ["ADMIN", "BOARD_MEMBER"], capability: "finance:manage", tour: "nav-budgets" },
      { to: "/finance/close", labelKey: "nav.monthlyClose", icon: <Wallet size={18} />, roles: ["ADMIN", "BOARD_MEMBER"], capability: "finance:manage", tour: "nav-monthly-close" },
      { to: "/resident/finances", labelKey: "nav.residentFinances", icon: <DollarSign size={18} />, roles: ["RESIDENT"], tour: "nav-resident-finances" },
    ],
  },
  {
    items: [
      { to: "/help", labelKey: "nav.help", icon: <HelpCircle size={18} />, roles: ["ADMIN", "BOARD_MEMBER", "RESIDENT"], tour: "nav-help" },
    ],
  },
];

export function AppLayout() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const role = (user?.role ?? "PUBLIC") as Role;
  const canSee = (i: NavItem) => i.roles.includes(role) || (i.capability && userHasCapability(user, i.capability));
  const visibleSections = navSections
    .map((section) => ({ ...section, items: section.items.filter(canSee) }))
    .filter((section) => section.items.length > 0);
  useFirstLoginTour();

  async function handleLogout() {
    await logout();
    navigate("/login");
  }

  function toggleLang() {
    const next = i18n.language.startsWith("es") ? "en" : "es";
    i18n.changeLanguage(next);
  }

  // On mobile the sidebar is an off-canvas drawer; collapse (icon rail) is desktop-only.
  const showLabels = !collapsed || mobileOpen;

  return (
    <div className="flex min-h-screen">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}
      <aside
        className={cn(
          "bg-primary text-white flex flex-col z-50 transition-transform md:transition-all",
          "fixed inset-y-0 left-0 w-64 md:static md:translate-x-0",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
          collapsed ? "md:w-16" : "md:w-64"
        )}
      >
        <div className="p-4 flex items-center gap-2 border-b border-primary-700">
          {/* Desktop collapse toggle */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-1 hover:bg-primary-700 rounded hidden md:block"
            aria-label={t("nav.toggleSidebar")}
          >
            <Menu size={20} />
          </button>
          {/* Mobile close */}
          <button
            onClick={() => setMobileOpen(false)}
            className="p-1 hover:bg-primary-700 rounded md:hidden"
            aria-label={t("common.close")}
          >
            <X size={20} />
          </button>
          {showLabels ? (
            <Link to="/dashboard" className="flex items-center gap-2 ml-1" onClick={() => setMobileOpen(false)}>
              <Logo variant="icon" color="white" className="h-8" />
              <span className="font-semibold text-sm leading-tight">{t("app.name")}</span>
            </Link>
          ) : (
            <Link to="/dashboard" className="ml-1">
              <Logo variant="icon" color="white" className="h-7" />
            </Link>
          )}
        </div>
        <nav className="flex-1 overflow-y-auto py-2">
          {visibleSections.map((section, sectionIndex) => (
            <div key={section.labelKey ?? `section-${sectionIndex}`}>
              {sectionIndex > 0 && (
                <div className={cn("border-t border-primary-700/60", showLabels ? "mt-3 mb-1 mx-4 pt-2" : "mt-2 mb-1 mx-3")}>
                  {section.labelKey && showLabels && (
                    <div className="px-0 pb-1 text-[11px] font-semibold uppercase tracking-wider text-primary-200/80">
                      {t(section.labelKey)}
                    </div>
                  )}
                </div>
              )}
              {section.items.map((item) => {
                const screen = showLabels ? SCREENS.find((s) => s.path === item.to) : undefined;
                return (
                  <div key={item.to} className="flex items-center">
                    <NavLink
                      to={item.to}
                      data-tour={item.tour}
                      onClick={() => setMobileOpen(false)}
                      className={({ isActive }) =>
                        cn(
                          "flex-1 min-w-0 flex items-center gap-3 px-4 py-2 text-sm hover:bg-primary-700 transition",
                          isActive && "bg-primary-700 border-l-4 border-accent"
                        )
                      }
                    >
                      {item.icon}
                      {showLabels && <span className="truncate">{t(item.labelKey)}</span>}
                    </NavLink>
                    {screen && (
                      <InfoPopover
                        className="mr-2 shrink-0"
                        descriptionEs={screen.purposeEs}
                        descriptionEn={screen.purposeEn}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </nav>
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 px-4 py-3 text-sm hover:bg-primary-700 border-t border-primary-700"
        >
          <LogOut size={18} />
          {showLabels && <span>{t("nav.logout")}</span>}
        </button>
      </aside>
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-slate-200 px-4 md:px-6 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen(true)}
              className="p-1 text-slate-600 hover:text-primary md:hidden"
              aria-label={t("nav.openMenu")}
            >
              <Menu size={22} />
            </button>
            <div className="text-sm text-slate-600 truncate">{t("app.tagline")}</div>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={toggleLang}
              className="flex items-center gap-1 text-sm text-slate-600 hover:text-primary"
              title={t("common.language")}
            >
              <Globe size={16} />
              <span className="uppercase">{i18n.language.slice(0, 2)}</span>
            </button>
            {user && (
              <div className="flex items-center gap-2 text-sm">
                <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-xs font-semibold">
                  {user.firstName[0]}{user.lastName[0]}
                </div>
                <div className="hidden md:block">
                  <div className="font-medium">{user.firstName} {user.lastName}</div>
                  <div className="text-xs text-slate-500">{user.role}</div>
                </div>
              </div>
            )}
          </div>
        </header>
        <main className="flex-1 p-6 overflow-auto bg-slate-50">
          <Outlet />
        </main>
      </div>
      {/* Floating shortcut to the role-flow reference — present on every authenticated
          page, opens in a new tab so it never interrupts whatever the user is doing. */}
      <a
        href="/flujos"
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t("nav.flujosAria")}
        title={t("nav.flujosAria")}
        className="fixed bottom-20 right-5 z-40 flex items-center gap-2 rounded-full bg-dorado px-4 py-3 text-sm font-medium text-white shadow-lg hover:bg-dorado-600 transition"
      >
        <GitBranch size={18} />
        <span className="hidden sm:inline">{t("nav.flujos")}</span>
      </a>
      <AssistantWidget />
    </div>
  );
}
